import { onBeforeUnmount, shallowRef } from "vue";
import { useAnalytics, type EntityType } from "./useAnalytics";

export interface CardImpressionInput {
  entity_type: EntityType;
  entity_id: string | number;
  entity_slug?: string;
  list_context: string;
  section?: string;
  position?: number;
  filters?: Record<string, any>;
  sort?: string;
}

export interface SectionViewInput {
  page_name: string;
  section: string;
  position?: number;
}

// Global registry of recorded impressions per view instance to prevent duplicates across component re-mounts
const recordedCardImpressions = new Set<string>();
const recordedSectionImpressions = new Set<string>();

const resolveDomElement = (target: any): Element | null => {
  if (!target) return null;
  if (typeof Element !== "undefined" && target instanceof Element) return target;
  if (target.$el && typeof Element !== "undefined" && target.$el instanceof Element) return target.$el;
  return null;
};

export function useImpressionTracking() {
  const { trackCardImpression, trackSectionView, generateExposureId, getViewInstanceId } = useAnalytics();

  // Keep track of active timers and unobserve functions for cleanup
  const activeTimers = new Map<Element, ReturnType<typeof setTimeout>>();
  const observerInstances: IntersectionObserver[] = [];

  const getImpressionKey = (entity_id: string | number, list_context: string, viewInstanceId: string) => {
    return `${viewInstanceId}::${list_context}::${entity_id}`;
  };

  const getSectionKey = (section: string, viewInstanceId: string) => {
    return `${viewInstanceId}::${section}`;
  };

  const observeCard = (
    el: any,
    input: CardImpressionInput,
    onImpressionFired?: (exposureId: string) => void
  ) => {
    const domEl = resolveDomElement(el);
    if (!domEl || import.meta.server || typeof IntersectionObserver === "undefined") {
      return;
    }

    const viewInstanceId = getViewInstanceId();
    const impressionKey = getImpressionKey(input.entity_id, input.list_context, viewInstanceId);

    if (recordedCardImpressions.has(impressionKey)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const target = entry.target;
          const isVisible50 = entry.isIntersecting && entry.intersectionRatio >= 0.5;

          if (isVisible50) {
            // If already pending, keep timer running
            if (!activeTimers.has(target)) {
              const timer = setTimeout(() => {
                activeTimers.delete(target);
                if (!recordedCardImpressions.has(impressionKey)) {
                  recordedCardImpressions.add(impressionKey);
                  const exposure_id = generateExposureId();
                  trackCardImpression({
                    ...input,
                    exposure_id,
                  });
                  onImpressionFired?.(exposure_id);
                }
                observer.unobserve(target);
              }, 1000);
              activeTimers.set(target, timer);
            }
          } else {
            // Visibility dropped below 50% before 1s completed, clear timer
            const pendingTimer = activeTimers.get(target);
            if (pendingTimer) {
              clearTimeout(pendingTimer);
              activeTimers.delete(target);
            }
          }
        }
      },
      {
        threshold: [0, 0.5, 1.0],
      }
    );

    observer.observe(domEl);
    observerInstances.push(observer);
  };

  const observeSection = (el: any, input: SectionViewInput) => {
    const domEl = resolveDomElement(el);
    if (!domEl || import.meta.server || typeof IntersectionObserver === "undefined") {
      return;
    }

    const viewInstanceId = getViewInstanceId();
    const sectionKey = getSectionKey(input.section, viewInstanceId);

    if (recordedSectionImpressions.has(sectionKey)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const target = entry.target;
          const isVisible50 = entry.isIntersecting && entry.intersectionRatio >= 0.5;

          if (isVisible50) {
            if (!activeTimers.has(target)) {
              const timer = setTimeout(() => {
                activeTimers.delete(target);
                if (!recordedSectionImpressions.has(sectionKey)) {
                  recordedSectionImpressions.add(sectionKey);
                  trackSectionView({
                    page_name: input.page_name,
                    section: input.section,
                    position: input.position,
                    view_instance_id: viewInstanceId,
                  });
                }
                observer.unobserve(target);
              }, 1000);
              activeTimers.set(target, timer);
            }
          } else {
            const pendingTimer = activeTimers.get(target);
            if (pendingTimer) {
              clearTimeout(pendingTimer);
              activeTimers.delete(target);
            }
          }
        }
      },
      {
        threshold: [0, 0.5, 1.0],
      }
    );

    observer.observe(domEl);
    observerInstances.push(observer);
  };

  const cleanup = () => {
    for (const timer of activeTimers.values()) {
      clearTimeout(timer);
    }
    activeTimers.clear();
    for (const obs of observerInstances) {
      obs.disconnect();
    }
    observerInstances.length = 0;
  };

  onBeforeUnmount(cleanup);

  return {
    observeCard,
    observeSection,
    cleanup,
  };
}
